import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  X, ChevronDown, ChevronRight, FileText, Save, Download, Upload,
  AlertTriangle, CheckCircle, Info, Search, Eye, Code, Edit3,
  MapPin, Calendar, User, Building, Globe, Database, Layers,
  RefreshCw, Wand2, AlertCircle, Navigation, List, FileCode
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-toastify';
import config from '../../config/config';
import './MetadataEditor.css';

// ISO 19115-3 Metadata Field Definitions with stages
const METADATA_STAGES = {
  identification: {
    label: 'Identifikasi',
    icon: FileText,
    description: 'Informasi identifikasi metadata',
    fields: [
      { path: 'mdb:metadataIdentifier > mcc:MD_Identifier > mcc:code > gco:CharacterString', label: 'File Identifier', required: true, placeholder: 'METADATA_ID_YYYYMMDD_LOCATION' },
      { path: 'mdb:defaultLocale > lan:PT_Locale > lan:language > lan:LanguageCode', label: 'Bahasa', required: true, type: 'select', options: ['ind', 'eng'], default: 'ind', attr: 'codeListValue' },
      { path: 'mdb:defaultLocale > lan:PT_Locale > lan:country > lan:CountryCode', label: 'Negara', required: false, type: 'select', options: ['ID'], default: 'ID', attr: 'codeListValue' },
      { path: 'mdb:defaultLocale > lan:PT_Locale > lan:characterEncoding > lan:MD_CharacterSetCode', label: 'Character Set', required: true, type: 'select', options: ['utf8', 'utf16', 'ascii'], default: 'utf8', attr: 'codeListValue' },
      { path: 'mdb:metadataScope > mdb:MD_MetadataScope > mdb:resourceScope > mcc:MD_ScopeCode', label: 'Scope', required: true, type: 'select', options: ['dataset', 'series', 'service'], default: 'dataset', attr: 'codeListValue' },
      { path: 'mdb:dateInfo > cit:CI_Date > cit:date > gco:DateTime', label: 'Tanggal Metadata', required: true, type: 'date' },
      { path: 'mdb:parentMetadata', label: 'Parent Metadata UUID', required: false, placeholder: 'UUID parent metadata', attr: 'uuidref' },
      { path: 'mdb:metadataStandard > cit:CI_Citation > cit:title > gco:CharacterString', label: 'Standar Metadata', required: false, placeholder: 'ISO 19115-3...' },
      { path: 'mdb:metadataStandard > cit:CI_Citation > cit:edition > gco:CharacterString', label: 'Edisi Standar', required: false, placeholder: '2014' },
    ]
  },
  contact: {
    label: 'Kontak',
    icon: User,
    description: 'Informasi kontak dan organisasi',
    repeatable: true,
    basePath: 'mdb:contact',
    fields: [
      { path: 'cit:CI_Responsibility > cit:role > cit:CI_RoleCode', label: 'Role', required: true, type: 'select', options: ['owner', 'custodian', 'distributor', 'pointOfContact', 'author', 'publisher', 'resourceProvider'], attr: 'codeListValue' },
      { path: 'cit:CI_Responsibility > cit:party > cit:CI_Organisation > cit:name > gco:CharacterString', label: 'Nama Organisasi', required: true, placeholder: 'Nama K/L atau Organisasi' },
      { path: 'cit:CI_Responsibility > cit:party > cit:CI_Organisation > cit:individual > cit:CI_Individual > cit:name > gco:CharacterString', label: 'Nama Individu', required: false, placeholder: 'Nama pejabat/penanggung jawab' },
      { path: 'cit:CI_Responsibility > cit:party > cit:CI_Organisation > cit:individual > cit:CI_Individual > cit:positionName > gco:CharacterString', label: 'Jabatan', required: false, placeholder: 'Jabatan penanggung jawab' },
      { path: 'cit:CI_Responsibility > cit:party > cit:CI_Organisation > cit:contactInfo > cit:CI_Contact > cit:phone > cit:CI_Telephone > cit:number > gco:CharacterString', label: 'Telepon', required: false, placeholder: '021-XXXXXXXX' },
      { path: 'cit:CI_Responsibility > cit:party > cit:CI_Organisation > cit:contactInfo > cit:CI_Contact > cit:phone > cit:CI_Telephone > cit:numberType > cit:CI_TelephoneTypeCode', label: 'Tipe Telepon', required: false, type: 'select', options: ['voice', 'facsimile'], attr: 'codeListValue' },
      { path: 'cit:CI_Responsibility > cit:party > cit:CI_Organisation > cit:contactInfo > cit:CI_Contact > cit:address > cit:CI_Address > cit:deliveryPoint > gco:CharacterString', label: 'Alamat', required: false, placeholder: 'Alamat lengkap' },
      { path: 'cit:CI_Responsibility > cit:party > cit:CI_Organisation > cit:contactInfo > cit:CI_Contact > cit:address > cit:CI_Address > cit:city > gco:CharacterString', label: 'Kota', required: false, placeholder: 'Nama Kota' },
      { path: 'cit:CI_Responsibility > cit:party > cit:CI_Organisation > cit:contactInfo > cit:CI_Contact > cit:address > cit:CI_Address > cit:administrativeArea > gco:CharacterString', label: 'Provinsi', required: false, placeholder: 'Nama Provinsi' },
      { path: 'cit:CI_Responsibility > cit:party > cit:CI_Organisation > cit:contactInfo > cit:CI_Contact > cit:address > cit:CI_Address > cit:postalCode > gco:CharacterString', label: 'Kode Pos', required: false, placeholder: '12345' },
      { path: 'cit:CI_Responsibility > cit:party > cit:CI_Organisation > cit:contactInfo > cit:CI_Contact > cit:address > cit:CI_Address > cit:country > lan:CountryCode', label: 'Negara (Kontak)', required: false, type: 'select', options: ['ID'], default: 'ID', attr: 'codeListValue' },
      { path: 'cit:CI_Responsibility > cit:party > cit:CI_Organisation > cit:contactInfo > cit:CI_Contact > cit:address > cit:CI_Address > cit:electronicMailAddress > gco:CharacterString', label: 'Email', required: false, placeholder: 'email@domain.go.id' },
      { path: 'cit:CI_Responsibility > cit:party > cit:CI_Organisation > cit:contactInfo > cit:CI_Contact > cit:hoursOfService > gco:CharacterString', label: 'Jam Layanan', required: false, placeholder: '08:00-16:00' },
    ]
  },
  spatialRepresentation: {
    label: 'Representasi Spasial',
    icon: Layers,
    description: 'Informasi representasi spasial data',
    fields: [
      { path: 'mdb:spatialRepresentationInfo > msr:MD_VectorSpatialRepresentation > msr:topologyLevel > msr:MD_TopologyLevelCode', label: 'Topology Level', required: false, type: 'select', options: ['geometryOnly', 'topology1D', 'topology2D', 'fullPlanarGraph', 'surfaceGraph', 'fullSurfaceGraph', 'fullTopology3D', 'abstract'], attr: 'codeListValue' },
      { path: 'mdb:spatialRepresentationInfo > msr:MD_VectorSpatialRepresentation > msr:geometricObjects > msr:MD_GeometricObjects > msr:geometricObjectType > msr:MD_GeometricObjectTypeCode', label: 'Tipe Geometri', required: true, type: 'select', options: ['point', 'curve', 'surface', 'solid', 'composite', 'complex'], attr: 'codeListValue' },
    ]
  },
  referenceSystem: {
    label: 'Sistem Referensi',
    icon: Globe,
    description: 'Sistem referensi koordinat',
    fields: [
      { path: 'mdb:referenceSystemInfo > mrs:MD_ReferenceSystem > mrs:referenceSystemIdentifier > mcc:MD_Identifier > mcc:code > gco:CharacterString', label: 'EPSG Code', required: true, placeholder: '4326', suggestions: ['4326', '32748', '32749', '32750', '23845', '23846'] },
      { path: 'mdb:referenceSystemInfo > mrs:MD_ReferenceSystem > mrs:referenceSystemIdentifier > mcc:MD_Identifier > mcc:codeSpace > gco:CharacterString', label: 'Code Space', required: false, default: 'EPSG' },
    ]
  },
  dataIdentification: {
    label: 'Identifikasi Data',
    icon: Database,
    description: 'Identifikasi dataset',
    fields: [
      { path: 'mdb:identificationInfo > mri:MD_DataIdentification > mri:citation > cit:CI_Citation > cit:title > gco:CharacterString', label: 'Judul Data', required: true, placeholder: 'Judul dataset' },
      { path: 'mdb:identificationInfo > mri:MD_DataIdentification > mri:citation > cit:CI_Citation > cit:alternateTitle > gco:CharacterString', label: 'Judul Alternatif', required: false, placeholder: 'Deskripsi singkat judul data' },
      { path: 'mdb:identificationInfo > mri:MD_DataIdentification > mri:citation > cit:CI_Citation > cit:edition > gco:CharacterString', label: 'Edisi', required: false, placeholder: '1' },
      { path: 'mdb:identificationInfo > mri:MD_DataIdentification > mri:citation > cit:CI_Citation > cit:identifier > mcc:MD_Identifier > mcc:code > gco:CharacterString', label: 'Resource Identifier', required: false, placeholder: 'ID dataset' },
      { path: 'special:citationDate:creation', label: 'Tanggal Pembuatan', required: false, type: 'dateByType', dateType: 'creation', citationPath: 'mdb:identificationInfo > mri:MD_DataIdentification > mri:citation > cit:CI_Citation' },
      { path: 'special:citationDate:publication', label: 'Tanggal Publikasi', required: false, type: 'dateByType', dateType: 'publication', citationPath: 'mdb:identificationInfo > mri:MD_DataIdentification > mri:citation > cit:CI_Citation' },
      { path: 'special:citationDate:revision', label: 'Tanggal Revisi', required: false, type: 'dateByType', dateType: 'revision', citationPath: 'mdb:identificationInfo > mri:MD_DataIdentification > mri:citation > cit:CI_Citation' },
      { path: 'mdb:identificationInfo > mri:MD_DataIdentification > mri:citation > cit:CI_Citation > cit:presentationForm > cit:CI_PresentationFormCode', label: 'Presentation Form', required: false, type: 'select', options: ['mapDigital', 'documentDigital', 'tableDigital', 'imageDigital', 'modelDigital', 'profileDigital', 'other'], attr: 'codeListValue' },
      { path: 'mdb:identificationInfo > mri:MD_DataIdentification > mri:citation > cit:CI_Citation > cit:onlineResource > cit:CI_OnlineResource > cit:linkage > gco:CharacterString', label: 'URL (Citation)', required: false, type: 'url', placeholder: 'https://...' },
      { path: 'mdb:identificationInfo > mri:MD_DataIdentification > mri:citation > cit:CI_Citation > cit:onlineResource > cit:CI_OnlineResource > cit:protocol > gco:CharacterString', label: 'Protokol (Citation)', required: false, placeholder: 'https' },
      { path: 'mdb:identificationInfo > mri:MD_DataIdentification > mri:citation > cit:CI_Citation > cit:onlineResource > cit:CI_OnlineResource > cit:name > gco:CharacterString', label: 'Nama Resource (Citation)', required: false, placeholder: 'Nama link' },
      { path: 'mdb:identificationInfo > mri:MD_DataIdentification > mri:citation > cit:CI_Citation > cit:onlineResource > cit:CI_OnlineResource > cit:function > cit:CI_OnLineFunctionCode', label: 'Fungsi Online Resource', required: false, type: 'select', options: ['information', 'download', 'search', 'order', 'offlineAccess'], attr: 'codeListValue' },
      { path: 'mdb:identificationInfo > mri:MD_DataIdentification > mri:abstract > gco:CharacterString', label: 'Abstrak', required: true, type: 'textarea', placeholder: 'Deskripsi lengkap tentang dataset' },
      { path: 'mdb:identificationInfo > mri:MD_DataIdentification > mri:purpose > gco:CharacterString', label: 'Tujuan', required: false, type: 'textarea', placeholder: 'Tujuan pembuatan dataset' },
      { path: 'mdb:identificationInfo > mri:MD_DataIdentification > mri:credit > gco:CharacterString', label: 'Kredit', required: false, placeholder: 'Organisasi pembuat data' },
      { path: 'mdb:identificationInfo > mri:MD_DataIdentification > mri:status > mcc:MD_ProgressCode', label: 'Status', required: true, type: 'select', options: ['completed', 'historicalArchive', 'obsolete', 'onGoing', 'planned', 'required', 'underDevelopment'], attr: 'codeListValue' },
      { path: 'mdb:identificationInfo > mri:MD_DataIdentification > mri:spatialRepresentationType > mcc:MD_SpatialRepresentationTypeCode', label: 'Tipe Representasi', required: true, type: 'select', options: ['vector', 'grid', 'textTable', 'tin', 'stereoModel', 'video'], attr: 'codeListValue' },
      { path: 'mdb:identificationInfo > mri:MD_DataIdentification > mri:spatialResolution > mri:MD_Resolution > mri:equivalentScale > mri:MD_RepresentativeFraction > mri:denominator > gco:Integer', label: 'Skala (denominator)', required: false, type: 'number', placeholder: '5000' },
    ]
  },
  extent: {
    label: 'Cakupan',
    icon: MapPin,
    description: 'Cakupan geografis data',
    fields: [
      { path: 'mdb:identificationInfo > mri:MD_DataIdentification > mri:extent > gex:EX_Extent > gex:description > gco:CharacterString', label: 'Deskripsi Cakupan', required: false, placeholder: 'Deskripsi area cakupan data' },
      { path: 'mdb:identificationInfo > mri:MD_DataIdentification > mri:extent > gex:EX_Extent > gex:geographicElement > gex:EX_GeographicBoundingBox > gex:westBoundLongitude > gco:Decimal', label: 'Batas Barat (Longitude)', required: true, type: 'number', placeholder: '95.0' },
      { path: 'mdb:identificationInfo > mri:MD_DataIdentification > mri:extent > gex:EX_Extent > gex:geographicElement > gex:EX_GeographicBoundingBox > gex:eastBoundLongitude > gco:Decimal', label: 'Batas Timur (Longitude)', required: true, type: 'number', placeholder: '141.0' },
      { path: 'mdb:identificationInfo > mri:MD_DataIdentification > mri:extent > gex:EX_Extent > gex:geographicElement > gex:EX_GeographicBoundingBox > gex:southBoundLatitude > gco:Decimal', label: 'Batas Selatan (Latitude)', required: true, type: 'number', placeholder: '-11.0' },
      { path: 'mdb:identificationInfo > mri:MD_DataIdentification > mri:extent > gex:EX_Extent > gex:geographicElement > gex:EX_GeographicBoundingBox > gex:northBoundLatitude > gco:Decimal', label: 'Batas Utara (Latitude)', required: true, type: 'number', placeholder: '6.0' },
    ]
  },
  topicCategory: {
    label: 'Kategori Topik',
    icon: List,
    description: 'Kategori topik ISO',
    fields: [
      { path: 'mdb:identificationInfo > mri:MD_DataIdentification > mri:topicCategory > mri:MD_TopicCategoryCode', label: 'Kategori Topik', required: true, type: 'multiselect', options: [
        'farming', 'biota', 'boundaries', 'climatologyMeteorologyAtmosphere', 
        'economy', 'elevation', 'environment', 'geoscientificInformation',
        'health', 'imageryBaseMapsEarthCover', 'intelligenceMilitary', 'inlandWaters',
        'location', 'oceans', 'planningCadastre', 'society', 'structure',
        'transportation', 'utilitiesCommunication'
      ]}
    ]
  },
  keywords: {
    label: 'Kata Kunci',
    icon: Search,
    description: 'Kata kunci untuk pencarian',
    fields: [
      { path: 'special:keywords:theme', label: 'Keyword (Theme)', required: false, type: 'typedKeywordList', keywordType: 'theme', placeholder: 'Tambah keyword theme', dataIdentificationPath: 'mdb:identificationInfo > mri:MD_DataIdentification' },
      { path: 'special:keywords:place', label: 'Keyword (Place)', required: false, type: 'typedKeywordList', keywordType: 'place', placeholder: 'Tambah keyword place', dataIdentificationPath: 'mdb:identificationInfo > mri:MD_DataIdentification' }
    ]
  },
  distribution: {
    label: 'Distribusi',
    icon: Download,
    description: 'Informasi distribusi data',
    fields: [
      { path: 'mdb:distributionInfo > mrd:MD_Distribution > mrd:distributionFormat > mrd:MD_Format > mrd:formatSpecificationCitation > cit:CI_Citation > cit:title > gco:CharacterString', label: 'Format', required: false, placeholder: 'Shapefile, GeoTIFF, dll' },
      { path: 'mdb:distributionInfo > mrd:MD_Distribution > mrd:transferOptions > mrd:MD_DigitalTransferOptions > mrd:onLine > cit:CI_OnlineResource > cit:linkage > gco:CharacterString', label: 'URL Distribusi', required: false, type: 'url', placeholder: 'https://...' },
      { path: 'mdb:distributionInfo > mrd:MD_Distribution > mrd:transferOptions > mrd:MD_DigitalTransferOptions > mrd:onLine > cit:CI_OnlineResource > cit:protocol > gco:CharacterString', label: 'Protokol', required: false, type: 'select', options: ['https', 'http', 'ftp', 'wms', 'wfs', 'wcs'] },
    ]
  },
  dataQuality: {
    label: 'Kualitas Data',
    icon: CheckCircle,
    description: 'Informasi kualitas data',
    fields: [
      { path: 'mdb:dataQualityInfo > mdq:DQ_DataQuality > mdq:scope > mcc:MD_Scope > mcc:level > mcc:MD_ScopeCode', label: 'Scope Level', required: false, type: 'select', options: ['dataset', 'series', 'feature', 'attribute'], attr: 'codeListValue' },
      { path: 'mdb:dataQualityInfo > mdq:DQ_DataQuality > mdq:report > mdq:DQ_CompletenessCommission > mdq:result > mdq:DQ_QuantitativeResult > mdq:value > gco:Record', label: 'Kelengkapan Data (%)', required: false, type: 'number', placeholder: '100' },
    ]
  },
  resourceConstraints: {
    label: 'Batasan Penggunaan',
    icon: AlertTriangle,
    description: 'Batasan dan lisensi penggunaan data',
    fields: [
      { path: 'mdb:identificationInfo > mri:MD_DataIdentification > mri:resourceConstraints > mco:MD_LegalConstraints > mco:accessConstraints > mco:MD_RestrictionCode', label: 'Batasan Akses', required: false, type: 'select', options: ['copyright', 'patent', 'trademark', 'license', 'intellectualPropertyRights', 'restricted', 'otherRestrictions'], attr: 'codeListValue' },
      { path: 'mdb:identificationInfo > mri:MD_DataIdentification > mri:resourceConstraints > mco:MD_LegalConstraints > mco:useConstraints > mco:MD_RestrictionCode', label: 'Batasan Penggunaan', required: false, type: 'select', options: ['copyright', 'patent', 'trademark', 'license', 'intellectualPropertyRights', 'restricted', 'otherRestrictions'], attr: 'codeListValue' },
      { path: 'mdb:identificationInfo > mri:MD_DataIdentification > mri:resourceConstraints > mco:MD_LegalConstraints > mco:otherConstraints > gco:CharacterString', label: 'Keterangan Batasan', required: false, type: 'textarea', placeholder: 'Deskripsi batasan penggunaan' },
    ]
  },
  maintenance: {
    label: 'Pemeliharaan',
    icon: RefreshCw,
    description: 'Informasi pemeliharaan dan update',
    fields: [
      { path: 'mdb:identificationInfo > mri:MD_DataIdentification > mri:resourceMaintenance > mmi:MD_MaintenanceInformation > mmi:maintenanceAndUpdateFrequency > mmi:MD_MaintenanceFrequencyCode', label: 'Frekuensi Update', required: false, type: 'select', options: ['continual', 'daily', 'weekly', 'fortnightly', 'monthly', 'quarterly', 'biannually', 'annually', 'asNeeded', 'irregular', 'notPlanned', 'unknown'], attr: 'codeListValue' },
    ]
  },
};

// Raster-specific fields
const RASTER_SPECIFIC_FIELDS = {
  spatialRepresentation: {
    fields: [
      { path: 'mdb:spatialRepresentationInfo > msr:MD_GridSpatialRepresentation > msr:numberOfDimensions > gco:Integer', label: 'Jumlah Dimensi', required: false, type: 'number', placeholder: '2' },
      { path: 'mdb:spatialRepresentationInfo > msr:MD_GridSpatialRepresentation > msr:cellGeometry > msr:MD_CellGeometryCode', label: 'Geometri Sel', required: false, type: 'select', options: ['point', 'area', 'voxel', 'stratum'], attr: 'codeListValue' },
    ]
  },
  dataIdentification: {
    fields: [
      { path: 'mdb:identificationInfo > mri:MD_DataIdentification > mri:spatialResolution > mri:MD_Resolution > mri:distance > gco:Distance', label: 'Resolusi Spasial (Distance)', required: false, type: 'number', placeholder: '30' },
      { path: 'special:rasterSpatialResolutionUom', label: 'Unit Resolusi (uom)', required: false, placeholder: 'm', type: 'attrFromPath', targetPath: 'mdb:identificationInfo > mri:MD_DataIdentification > mri:spatialResolution > mri:MD_Resolution > mri:distance > gco:Distance', attr: 'uom' },
    ]
  },
  contentInfo: {
    label: 'Informasi Konten',
    icon: Layers,
    description: 'Informasi konten raster',
    fields: [
      { path: 'mdb:contentInfo > mrc:MD_CoverageDescription > mrc:attributeDescription > gco:RecordType', label: 'Deskripsi Atribut', required: false, placeholder: 'Deskripsi atribut raster' },
      { path: 'mdb:contentInfo > mrc:MD_CoverageDescription > mrc:contentType > mrc:MD_CoverageContentTypeCode', label: 'Tipe Konten', required: false, type: 'select', options: ['image', 'thematicClassification', 'physicalMeasurement', 'auxilliaryInformation', 'qualityInformation', 'referenceInformation', 'modelResult', 'coordinate'], attr: 'codeListValue' },
    ]
  }
};

const MetadataEditor = ({ 
  isOpen, 
  onClose, 
  metadataFile, // File object or URL or XML content string
  onSave,
  readOnly = false,
  noTiket = null,
  documentType = 'metadata',
  existingMetadata = null,
  mode = 'edit',
  disableSuggestions = false,
  disableRemoteTemplates = false,
  localTemplates = null,
  hideTemplateBar = false,
  disableXmlUpload = false,
  initialHasModified = false,
  initialHasStandardized = false
}) => {
  const [activeView, setActiveView] = useState('form');
  const [activeStage, setActiveStage] = useState('identification');
  const [metadataValues, setMetadataValues] = useState({});
  const [originalXml, setOriginalXml] = useState('');
  const [parsedXml, setParsedXml] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dataType, setDataType] = useState('vector');
  const [placeholders, setPlaceholders] = useState([]);
  const [showPlaceholderWarning, setShowPlaceholderWarning] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState({});
  const [validationErrors, setValidationErrors] = useState([]);
  const [hasModified, setHasModified] = useState(false); 
  const [hasStandardized, setHasStandardized] = useState(false);
  const [detectedFormat, setDetectedFormat] = useState('unknown'); 
  const [editableXml, setEditableXml] = useState(''); // For XML view editing
  const [xmlError, setXmlError] = useState(''); // For XML parsing errors
  const [editingNode, setEditingNode] = useState(null); // For tree node inline editing
  const [editingNodeValue, setEditingNodeValue] = useState(''); // Value being edited in tree
  const [repeatableStageCounts, setRepeatableStageCounts] = useState({});
  const [pendingListInputs, setPendingListInputs] = useState({});
  const [pendingMultiSelectAdds, setPendingMultiSelectAdds] = useState({});
  const fileInputRef = useRef(null);
  const fieldRefs = useRef({});
  const formContainerRef = useRef(null);
  const lastSyncedXmlRef = useRef('');

  const parsePathPart = useCallback((part) => {
    const match = part.match(/^(.*?)(?:\[(\d+)\])?$/);
    const name = match?.[1] || part;
    const index = match?.[2] !== undefined ? parseInt(match[2], 10) : undefined;
    const [prefix, localName] = name.includes(':') ? name.split(':') : ['', name];
    return { raw: part, name, prefix, localName, index };
  }, []);

  const getMetadataRoot = useCallback((xmlDoc) => {
    if (!xmlDoc) return null;
    return (
      xmlDoc.querySelector('mdb\\:MD_Metadata, gmd\\:MD_Metadata, MD_Metadata') ||
      xmlDoc.documentElement ||
      null
    );
  }, []);

  const getDirectChildrenByLocalName = useCallback((parent, localName) => {
    if (!parent || !localName) return [];
    const element = parent.nodeType === 9 ? parent.documentElement : parent;
    if (!element || !element.children) return [];
    return Array.from(element.children).filter((child) => child?.localName === localName);
  }, []);

  const createQualifiedElement = useCallback((doc, qualifiedName) => {
    if (!doc || !qualifiedName) return null;
    const [prefix, localName] = qualifiedName.includes(':') ? qualifiedName.split(':') : [null, qualifiedName];
    const nsUri = prefix ? (doc.documentElement?.lookupNamespaceURI(prefix) || null) : null;
    try {
      if (nsUri) return doc.createElementNS(nsUri, `${prefix}:${localName}`);
      return doc.createElement(qualifiedName);
    } catch (e) {
      return doc.createElement(qualifiedName);
    }
  }, []);

  const createChildElement = useCallback((doc, name, parentForNamespace) => {
    if (!doc || !name) return null;
    if (name.includes(':')) return createQualifiedElement(doc, name);
    const parentPrefix = parentForNamespace?.prefix || null;
    const parentNs = parentForNamespace?.namespaceURI || null;
    try {
      if (parentNs && parentPrefix) return doc.createElementNS(parentNs, `${parentPrefix}:${name}`);
      if (parentNs && !parentPrefix) return doc.createElementNS(parentNs, name);
      return doc.createElement(name);
    } catch (e) {
      return doc.createElement(name);
    }
  }, [createQualifiedElement]);

  const findFirstDescendantByLocalName = useCallback((parent, localName) => {
    if (!parent || !localName) return null;
    const root = parent.nodeType === 9 ? parent.documentElement : parent;
    if (!root) return null;

    const stack = [root];
    while (stack.length > 0) {
      const node = stack.shift();
      if (!node) continue;
      if (node.nodeType === 1 && node.localName === localName) return node;

      if (node.children && node.children.length > 0) {
        stack.push(...Array.from(node.children));
      }
    }
    return null;
  }, []);

  const resolveElementByPath = useCallback((xmlDoc, path, options = {}) => {
    const { create = false } = options;
    if (!xmlDoc || !path) return null;

    const root = getMetadataRoot(xmlDoc);
    if (!root) return null;

    const parts = path.split(' > ').map((p) => p.trim()).filter(Boolean);
    let current = root;

    for (let i = 0; i < parts.length; i++) {
      const seg = parsePathPart(parts[i]);
      const localName = seg.localName;
      const desiredIndex = seg.index ?? 0;

      let matches = getDirectChildrenByLocalName(current, localName);

      if ((!matches || matches.length === 0) && current?.querySelector) {
        const selector = seg.prefix ? `${seg.prefix}\\:${localName}, ${localName}` : `${localName}`;
        let any = null;
        try {
          any = current.querySelector(selector);
        } catch (e) {
          any = null;
        }
        if (!any) {
          any = findFirstDescendantByLocalName(current, localName);
        }
        if (any) matches = [any];
      }

      if ((!matches || matches.length <= desiredIndex) && create) {
        const doc = xmlDoc.nodeType === 9 ? xmlDoc : xmlDoc.ownerDocument;
        const nsUri = seg.prefix
          ? (doc.documentElement?.lookupNamespaceURI(seg.prefix) || null)
          : null;

        while (!matches || matches.length <= desiredIndex) {
          const newEl = nsUri
            ? doc.createElementNS(nsUri, seg.prefix ? `${seg.prefix}:${localName}` : localName)
            : doc.createElement(seg.prefix ? `${seg.prefix}:${localName}` : localName);

          current.appendChild(newEl);
          matches = getDirectChildrenByLocalName(current, localName);
        }
      }

      if (!matches || matches.length <= desiredIndex) return null;
      current = matches[desiredIndex];
    }

    return current;
  }, [findFirstDescendantByLocalName, getDirectChildrenByLocalName, getMetadataRoot, parsePathPart]);

  const resolveElementsByPath = useCallback((xmlDoc, path) => {
    try {
      if (!xmlDoc || !path) return [];
      const root = getMetadataRoot(xmlDoc);
      if (!root) return [];

      const parts = path.split(' > ').map((p) => p.trim()).filter(Boolean);
      let currentNodes = [root];

      for (const part of parts) {
        const { localName, index } = parsePathPart(part);
        const nextNodes = [];
        currentNodes.forEach((node) => {
          const matches = getDirectChildrenByLocalName(node, localName);
          if (index !== undefined) {
            const picked = matches[index];
            if (picked) nextNodes.push(picked);
          } else {
            nextNodes.push(...matches);
          }
        });

        if (nextNodes.length === 0) return [];
        currentNodes = nextNodes;
      }

      return currentNodes;
    } catch (e) {
      return [];
    }
  }, [getDirectChildrenByLocalName, getMetadataRoot, parsePathPart]);

  const getFieldDefinitionForPath = useCallback((path) => {
    const stages = { ...METADATA_STAGES };
    if (dataType === 'raster') {
      Object.keys(RASTER_SPECIFIC_FIELDS).forEach((key) => {
        if (stages[key]) {
          stages[key] = {
            ...stages[key],
            fields: [...(stages[key].fields || []), ...(RASTER_SPECIFIC_FIELDS[key].fields || [])]
          };
        } else {
          stages[key] = RASTER_SPECIFIC_FIELDS[key];
        }
      });
    }

    for (const stage of Object.values(stages)) {
      if (!stage?.fields) continue;
      if (stage.repeatable && stage.basePath) {
        const match = path.match(
          new RegExp(
            `^${stage.basePath.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')}\\[\\d+\\] > (.*)$`
          )
        );
        if (!match) continue;
        const rest = match[1];
        const found = stage.fields.find((f) => f.path === rest);
        if (found) return found;
        continue;
      }

      const found = stage.fields.find((f) => f.path === path);
      if (found) return found;
    }
    return null;
  }, [dataType]);

  const setMultiSelectValuesToPath = useCallback((xmlDoc, path, value) => {
    try {
      const selections = String(value)
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);

      const parts = path.split(' > ').map((p) => p.trim()).filter(Boolean);
      if (parts.length < 2) return false;

      const wrapperPart = parts[parts.length - 2];
      const wrapperLocalName = parsePathPart(wrapperPart).localName;
      const parentPath = parts.slice(0, parts.length - 2).join(' > ');

      const parentEl = resolveElementByPath(xmlDoc, parentPath, { create: true });
      if (!parentEl) return false;

      const existing = getDirectChildrenByLocalName(parentEl, wrapperLocalName);
      existing.forEach((node) => parentEl.removeChild(node));

      const codePart = parts[parts.length - 1];
      const codeName = parsePathPart(codePart).name;

      const doc = xmlDoc.nodeType === 9 ? xmlDoc : xmlDoc.ownerDocument;

      selections.forEach((sel) => {
        const wrapperName = parsePathPart(wrapperPart).name;
        const wrapperEl = createChildElement(doc, wrapperName, parentEl);
        const codeEl = createChildElement(doc, codeName, wrapperEl);
        codeEl.textContent = sel;
        wrapperEl.appendChild(codeEl);
        parentEl.appendChild(wrapperEl);
      });

      return true;
    } catch (e) {
      return false;
    }
  }, [createChildElement, getDirectChildrenByLocalName, parsePathPart, resolveElementByPath]);

  const extractCitationDateByType = useCallback((xmlDoc, field) => {
    try {
      const citationEl =
        resolveElementByPath(xmlDoc, field.citationPath, { create: false }) ||
        resolveElementByPath(xmlDoc, getIso19139Path(field.citationPath), { create: false }) ||
        findFirstDescendantByLocalName(getMetadataRoot(xmlDoc), 'CI_Citation');
      if (!citationEl) return '';
      const dateWrappers = getDirectChildrenByLocalName(citationEl, 'date');
      for (const wrapper of dateWrappers) {
        const typeEl = findFirstDescendantByLocalName(wrapper, 'CI_DateTypeCode');
        const typeValue = typeEl?.getAttribute('codeListValue') || typeEl?.textContent?.trim() || '';
        if (typeValue !== field.dateType) continue;

        const dateTimeEl =
          findFirstDescendantByLocalName(wrapper, 'DateTime') ||
          findFirstDescendantByLocalName(wrapper, 'Date');
        return dateTimeEl?.textContent?.trim() || '';
      }
      return '';
    } catch (e) {
      return '';
    }
  }, [findFirstDescendantByLocalName, getDirectChildrenByLocalName, getIso19139Path, getMetadataRoot, resolveElementByPath]);

  const setCitationDateByType = useCallback((xmlDoc, field, value) => {
    try {
      const citationEl =
        resolveElementByPath(xmlDoc, field.citationPath, { create: true }) ||
        resolveElementByPath(xmlDoc, getIso19139Path(field.citationPath), { create: true });
      if (!citationEl) return false;

      const dateWrappers = getDirectChildrenByLocalName(citationEl, 'date');
      let wrapper = null;
      for (const w of dateWrappers) {
        const typeEl = findFirstDescendantByLocalName(w, 'CI_DateTypeCode');
        const typeValue = typeEl?.getAttribute('codeListValue') || typeEl?.textContent?.trim() || '';
        if (typeValue === field.dateType) {
          wrapper = w;
          break;
        }
      }

      const doc = xmlDoc.nodeType === 9 ? xmlDoc : xmlDoc.ownerDocument;
      if (!wrapper) {
        const isLegacy = isIso19139Document(xmlDoc);
        const wrapperName = isLegacy ? 'date' : 'cit:date';
        const ciDateName = isLegacy ? 'CI_Date' : 'cit:CI_Date';
        const dateName = isLegacy ? 'date' : 'cit:date';

        wrapper = isLegacy
          ? createChildElement(doc, wrapperName, citationEl)
          : createQualifiedElement(doc, wrapperName);

        const ciDate = isLegacy
          ? createChildElement(doc, ciDateName, wrapper)
          : createQualifiedElement(doc, ciDateName);

        const dateEl = isLegacy
          ? createChildElement(doc, dateName, ciDate)
          : createQualifiedElement(doc, dateName);
        const dateTimeEl = createQualifiedElement(doc, 'gco:DateTime');
        dateTimeEl.textContent = value;
        dateEl.appendChild(dateTimeEl);

        const dateTypeEl = isLegacy
          ? createChildElement(doc, 'dateType', ciDate)
          : createQualifiedElement(doc, 'cit:dateType');

        const codeEl = isLegacy
          ? createChildElement(doc, 'CI_DateTypeCode', dateTypeEl)
          : createQualifiedElement(doc, 'cit:CI_DateTypeCode');
        codeEl.setAttribute('codeListValue', field.dateType);
        codeEl.textContent = field.dateType;
        dateTypeEl.appendChild(codeEl);

        ciDate.appendChild(dateEl);
        ciDate.appendChild(dateTypeEl);
        wrapper.appendChild(ciDate);
        citationEl.appendChild(wrapper);
        return true;
      }

      const dateTimeEl =
        findFirstDescendantByLocalName(wrapper, 'DateTime') ||
        findFirstDescendantByLocalName(wrapper, 'Date');
      if (dateTimeEl) {
        dateTimeEl.textContent = value;
        return true;
      }

      const ciDate = findFirstDescendantByLocalName(wrapper, 'CI_Date');
      if (!ciDate) return false;
      const dateEl = findFirstDescendantByLocalName(ciDate, 'date') || createChildElement(doc, 'date', ciDate);
      if (!dateEl.parentNode) ciDate.appendChild(dateEl);
      const newDateTime = createQualifiedElement(doc, 'gco:DateTime');
      newDateTime.textContent = value;
      dateEl.appendChild(newDateTime);
      return true;
    } catch (e) {
      return false;
    }
  }, [createChildElement, createQualifiedElement, findFirstDescendantByLocalName, getDirectChildrenByLocalName, getIso19139Path, isIso19139Document, resolveElementByPath]);

  const extractTypedKeywordList = useCallback((xmlDoc, field) => {
    try {
      const dataIdEl =
        resolveElementByPath(xmlDoc, field.dataIdentificationPath, { create: false }) ||
        resolveElementByPath(xmlDoc, getIso19139Path(field.dataIdentificationPath), { create: false }) ||
        findFirstDescendantByLocalName(getMetadataRoot(xmlDoc), 'MD_DataIdentification');
      if (!dataIdEl) return '';
      const descriptive = getDirectChildrenByLocalName(dataIdEl, 'descriptiveKeywords');
      const values = [];
      descriptive.forEach((dk) => {
        const mdKeywords = findFirstDescendantByLocalName(dk, 'MD_Keywords');
        if (!mdKeywords) return;
        const typeEl = findFirstDescendantByLocalName(mdKeywords, 'MD_KeywordTypeCode');
        const typeValue = typeEl?.getAttribute('codeListValue') || typeEl?.textContent?.trim() || '';
        const effectiveType = typeValue || (field.keywordType === 'theme' ? 'theme' : '');
        if (effectiveType !== field.keywordType) return;

        const keywordNodes = getDirectChildrenByLocalName(mdKeywords, 'keyword');
        keywordNodes.forEach((kwNode) => {
          const cs = findFirstDescendantByLocalName(kwNode, 'CharacterString');
          const txt = cs?.textContent?.trim();
          if (txt) values.push(txt);
        });
      });
      return values.join(',');
    } catch (e) {
      return '';
    }
  }, [findFirstDescendantByLocalName, getDirectChildrenByLocalName, getIso19139Path, getMetadataRoot, resolveElementByPath]);

  const setTypedKeywordList = useCallback((xmlDoc, field, value) => {
    try {
      const items = String(value)
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);

      const dataIdEl =
        resolveElementByPath(xmlDoc, field.dataIdentificationPath, { create: true }) ||
        resolveElementByPath(xmlDoc, getIso19139Path(field.dataIdentificationPath), { create: true });
      if (!dataIdEl) return false;

      const descriptive = getDirectChildrenByLocalName(dataIdEl, 'descriptiveKeywords');
      descriptive.forEach((dk) => {
        const mdKeywords = findFirstDescendantByLocalName(dk, 'MD_Keywords');
        if (!mdKeywords) return;
        const typeEl = findFirstDescendantByLocalName(mdKeywords, 'MD_KeywordTypeCode');
        const typeValue = typeEl?.getAttribute('codeListValue') || typeEl?.textContent?.trim() || '';
        const shouldRemove = field.keywordType === 'theme'
          ? (!typeValue || typeValue === 'theme')
          : typeValue === field.keywordType;
        if (shouldRemove) {
          dataIdEl.removeChild(dk);
        }
      });

      if (items.length === 0) {
        return true;
      }

      const doc = xmlDoc.nodeType === 9 ? xmlDoc : xmlDoc.ownerDocument;
      const isLegacy = isIso19139Document(xmlDoc);
      const dk = isLegacy
        ? createChildElement(doc, 'descriptiveKeywords', dataIdEl)
        : createQualifiedElement(doc, 'mri:descriptiveKeywords');

      const mdKeywords = isLegacy
        ? createChildElement(doc, 'MD_Keywords', dk)
        : createQualifiedElement(doc, 'mri:MD_Keywords');

      items.forEach((it) => {
        const kw = isLegacy
          ? createChildElement(doc, 'keyword', mdKeywords)
          : createQualifiedElement(doc, 'mri:keyword');
        const cs = createQualifiedElement(doc, 'gco:CharacterString');
        cs.textContent = it;
        kw.appendChild(cs);
        mdKeywords.appendChild(kw);
      });

      const typeWrap = isLegacy
        ? createChildElement(doc, 'type', mdKeywords)
        : createQualifiedElement(doc, 'mri:type');

      const typeCode = isLegacy
        ? createChildElement(doc, 'MD_KeywordTypeCode', typeWrap)
        : createQualifiedElement(doc, 'mri:MD_KeywordTypeCode');

      typeCode.setAttribute('codeListValue', field.keywordType);
      typeCode.textContent = field.keywordType;
      typeWrap.appendChild(typeCode);
      mdKeywords.appendChild(typeWrap);
      dk.appendChild(mdKeywords);
      dataIdEl.appendChild(dk);
      return true;
    } catch (e) {
      return false;
    }
  }, [createChildElement, createQualifiedElement, findFirstDescendantByLocalName, getDirectChildrenByLocalName, getIso19139Path, isIso19139Document, resolveElementByPath]);

  const extractValueForField = useCallback((xmlDoc, field, fullPath) => {
    if (field.type === 'attrFromPath') {
      return getValueFromPath(xmlDoc, field.targetPath, field.attr);
    }

    if (field.type === 'multiselect' || field.type === 'list') {
      const nodes = resolveElementsByPath(xmlDoc, fullPath || field.path);
      const extracted = nodes
        .map((n) => {
          if (field.attr) return n.getAttribute(field.attr) || n.getAttribute('codeListValue') || '';
          return n.textContent?.trim() || n.getAttribute('codeListValue') || '';
        })
        .filter(Boolean);
      return extracted.join(',');
    }

    if (field.type === 'dateByType') {
      return extractCitationDateByType(xmlDoc, field);
    }

    if (field.type === 'typedKeywordList') {
      return extractTypedKeywordList(xmlDoc, field);
    }

    return getValueFromPath(xmlDoc, fullPath || field.path, field.attr);
  }, [extractCitationDateByType, extractTypedKeywordList, resolveElementsByPath]);

  const setListValuesToPath = useCallback((xmlDoc, path, value) => {
    try {
      const items = String(value)
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);

      const parts = path.split(' > ').map((p) => p.trim()).filter(Boolean);
      if (parts.length < 2) return false;

      const listItemPart = parts[parts.length - 2];
      const listItemLocalName = parsePathPart(listItemPart).localName;
      const parentPath = parts.slice(0, parts.length - 2).join(' > ');
      const parentEl = resolveElementByPath(xmlDoc, parentPath, { create: true });
      if (!parentEl) return false;

      const existing = getDirectChildrenByLocalName(parentEl, listItemLocalName);
      existing.forEach((node) => parentEl.removeChild(node));

      const textPart = parts[parts.length - 1];
      const listItemName = parsePathPart(listItemPart).name;
      const textName = parsePathPart(textPart).name;

      const doc = xmlDoc.nodeType === 9 ? xmlDoc : xmlDoc.ownerDocument;

      items.forEach((item) => {
        const itemEl = createChildElement(doc, listItemName, parentEl);
        const textEl = createQualifiedElement(doc, textName);
        textEl.textContent = item;
        itemEl.appendChild(textEl);
        parentEl.appendChild(itemEl);
      });

      return true;
    } catch (e) {
      return false;
    }
  }, [createChildElement, createQualifiedElement, getDirectChildrenByLocalName, parsePathPart, resolveElementByPath]);

  const countRepeatableNodes = useCallback((xmlDoc, basePath) => {
    if (!xmlDoc || !basePath) return 0;
    const root = getMetadataRoot(xmlDoc);
    if (!root) return 0;
    const seg = parsePathPart(basePath);
    const direct = getDirectChildrenByLocalName(root, seg.localName);
    if (direct.length > 0) return direct.length;
    const any = xmlDoc.getElementsByTagName(seg.localName);
    return any?.length || 0;
  }, [getDirectChildrenByLocalName, getMetadataRoot, parsePathPart]);

  const ensureRepeatableCount = useCallback((xmlDoc, basePath, desiredCount) => {
    if (!xmlDoc || !basePath) return;
    const root = getMetadataRoot(xmlDoc);
    if (!root) return;
    const seg = parsePathPart(basePath);
    const existing = getDirectChildrenByLocalName(root, seg.localName);

    if (existing.length === 0 && desiredCount > 0) {
      resolveElementByPath(xmlDoc, `${basePath}[0]`, { create: true });
    }

    let current = getDirectChildrenByLocalName(root, seg.localName);
    if (current.length === 0) return;

    while (current.length < desiredCount) {
      const clone = current[0].cloneNode(true);
      root.appendChild(clone);
      current = getDirectChildrenByLocalName(root, seg.localName);
    }

    while (current.length > desiredCount) {
      const last = current[current.length - 1];
      last?.parentNode?.removeChild(last);
      current = getDirectChildrenByLocalName(root, seg.localName);
    }
  }, [getDirectChildrenByLocalName, getMetadataRoot, parsePathPart, resolveElementByPath]);

  // Load metadata from file or URL
  useEffect(() => {
    if (isOpen && metadataFile) {
      loadMetadata();
    }
  }, [isOpen, metadataFile]);

  // Load suggestions from existing system data (hanya jika tidak readOnly)
  useEffect(() => {
    if (isOpen && !readOnly && !disableSuggestions) {
      loadSuggestions();
    }
  }, [isOpen, readOnly, disableSuggestions]);

  // Recalculate placeholders when metadataValues change
  useEffect(() => {
    recalculatePlaceholders();
  }, [metadataValues, dataType, repeatableStageCounts]);

  useEffect(() => {
    if (!parsedXml) return;
    if (!hasModified) return;
    if (activeView === 'xml' && editableXml && editableXml.trim() !== '') return;

    const xmlDoc = parsedXml.cloneNode(true);

    Object.entries(METADATA_STAGES).forEach(([stageKey, stage]) => {
      if (stage.repeatable && stage.basePath) {
        const desiredCount = repeatableStageCounts[stageKey] || 1;
        ensureRepeatableCount(xmlDoc, stage.basePath, desiredCount);
      }
    });

    Object.entries(metadataValues).forEach(([path, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        const fieldDef = getFieldDefinitionForPath(path);
        setValueToPath(xmlDoc, path, value, fieldDef);
      }
    });

    const serializer = new XMLSerializer();
    const serialized = serializer.serializeToString(xmlDoc);
    if (serialized === lastSyncedXmlRef.current) return;
    lastSyncedXmlRef.current = serialized;
    setParsedXml(xmlDoc);
  }, [activeView, editableXml, ensureRepeatableCount, hasModified, metadataValues, parsedXml, repeatableStageCounts]);

  const recalculatePlaceholders = useCallback(() => {
    const foundPlaceholders = [];
    const stages = { ...METADATA_STAGES };
    
    if (dataType === 'raster') {
      Object.keys(RASTER_SPECIFIC_FIELDS).forEach(key => {
        if (stages[key]) {
          stages[key] = {
            ...stages[key],
            fields: [...stages[key].fields, ...RASTER_SPECIFIC_FIELDS[key].fields]
          };
        } else {
          stages[key] = RASTER_SPECIFIC_FIELDS[key];
        }
      });
    }

    Object.entries(stages).forEach(([stageKey, stage]) => {
      if (!stage.fields) return;

      if (stage.repeatable && stage.basePath) {
        const count = repeatableStageCounts[stageKey] || 1;
        for (let i = 0; i < count; i++) {
          stage.fields.forEach((field) => {
            const fullPath = `${stage.basePath}[${i}] > ${field.path}`;
            const value = metadataValues[fullPath] || '';
            const isPlaceholder = value.includes('{{') || value.includes('[PLACEHOLDER]');
            const isEmptyRequired = field.required && (!value || value.trim() === '');

            if (isPlaceholder || isEmptyRequired) {
              foundPlaceholders.push({
                stage: stageKey,
                field: `${field.label} (Kontak #${i + 1})`,
                path: fullPath,
                isPlaceholder,
                isEmpty: isEmptyRequired
              });
            }
          });
        }
        return;
      }

      stage.fields.forEach((field) => {
        const value = metadataValues[field.path] || '';
        const isPlaceholder = value.includes('{{') || value.includes('[PLACEHOLDER]');
        const isEmptyRequired = field.required && (!value || value.trim() === '');

        if (isPlaceholder || isEmptyRequired) {
          foundPlaceholders.push({
            stage: stageKey,
            field: field.label,
            path: field.path,
            isPlaceholder,
            isEmpty: isEmptyRequired
          });
        }
      });
    });

    setPlaceholders(foundPlaceholders);
  }, [metadataValues, dataType, repeatableStageCounts]);

  const loadSuggestions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${config.API_URL}/metadata/suggestions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSuggestions(response.data);
    } catch (error) {
      console.log('Could not load suggestions:', error);
    }
  };

  const loadMetadata = async () => {
    setLoading(true);
    try {
      let xmlContent = '';
      
      if (typeof metadataFile === 'string') {
        // Check if it's XML content or a URL
        const trimmed = metadataFile.trim();
        if (trimmed.startsWith('<?xml') || trimmed.startsWith('<mdb:') || trimmed.startsWith('<')) {
          xmlContent = metadataFile;
        } else if (metadataFile.startsWith('http://') || metadataFile.startsWith('https://')) {
          // It's a URL
          const token = localStorage.getItem('token');
          const response = await axios.get(`${config.API_URL}/metadata/download`, {
            params: { url: metadataFile, noTiket },
            headers: { 'Authorization': `Bearer ${token}` },
            responseType: 'text'
          });
          xmlContent = response.data;
        } else {
          // Assume it's XML content
          xmlContent = metadataFile;
        }
      } else if (metadataFile instanceof File) {
        if (!metadataFile.name.toLowerCase().endsWith('.xml')) {
          toast.error('Hanya file XML yang didukung untuk metadata editor');
          onClose();
          return;
        }
        xmlContent = await metadataFile.text();
      }

      setOriginalXml(xmlContent);
      parseXmlContent(xmlContent);

      // Allow callers (e.g. create-new flow) to show placeholder navigation immediately
      if (initialHasModified) {
        setHasModified(true);
      }
      if (initialHasStandardized) {
        setHasStandardized(true);
      }
    } catch (error) {
      console.error('Error loading metadata:', error);
      toast.error('Gagal memuat metadata');
    } finally {
      setLoading(false);
    }
  };

  const parseXmlContent = (xmlContent) => {
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlContent, 'text/xml');
      
      const parseError = xmlDoc.querySelector('parsererror');
      if (parseError) {
        throw new Error('Invalid XML format');
      }

      setParsedXml(xmlDoc);
      
      // Detect ISO format - check root element and also look inside for MD_Metadata
      const rootElement = xmlDoc.documentElement;
      const rootName = rootElement.tagName || rootElement.nodeName;
      
      // Check for ISO 19115-3 (mdb namespace)
      if (rootName.includes('mdb:') || rootName === 'mdb:MD_Metadata' ||
          xmlDoc.querySelector('mdb\\:MD_Metadata, mdb\\:metadataIdentifier')) {
        setDetectedFormat('iso19115-3');
      } 
      // Check for ISO 19139 (gmd namespace) - also check inside wrapper elements like CSW response
      else if (rootName === 'MD_Metadata' || rootName.includes('gmd:') ||
               xmlDoc.querySelector('gmd\\:MD_Metadata, MD_Metadata') ||
               xmlDoc.querySelector('gmd\\:fileIdentifier, fileIdentifier')) {
        setDetectedFormat('iso19139');
      } else {
        setDetectedFormat('unknown');
      }

      // Auto-detect data type
      const spatialRepType = xmlDoc.querySelector('mri\\:spatialRepresentationType, spatialRepresentationType, MD_SpatialRepresentationTypeCode');
      if (spatialRepType) {
        const typeValue = spatialRepType.getAttribute('codeListValue') || spatialRepType.textContent;
        if (typeValue === 'grid') {
          setDataType('raster');
        } else {
          setDataType('vector');
        }
      }

      const gridRep = xmlDoc.querySelector('msr\\:MD_GridSpatialRepresentation, MD_GridSpatialRepresentation');
      if (gridRep) {
        setDataType('raster');
      }

      // Reset modification state when loading new file
      setHasModified(false);
      setHasStandardized(false);
      
      extractMetadataValues(xmlDoc);
    } catch (error) {
      console.error('Error parsing XML:', error);
      toast.error('Gagal mem-parse file XML');
    }
  };

  const extractMetadataValues = (xmlDoc) => {
    const values = {};

    const newRepeatableCounts = {};

    const stages = { ...METADATA_STAGES };
    if (dataType === 'raster') {
      Object.keys(RASTER_SPECIFIC_FIELDS).forEach(key => {
        if (stages[key]) {
          stages[key] = {
            ...stages[key],
            fields: [...stages[key].fields, ...RASTER_SPECIFIC_FIELDS[key].fields]
          };
        } else {
          stages[key] = RASTER_SPECIFIC_FIELDS[key];
        }
      });
    }

    Object.entries(stages).forEach(([stageKey, stage]) => {
      if (!stage.fields) return;

      if (stage.repeatable && stage.basePath) {
        const count = Math.max(1, countRepeatableNodes(xmlDoc, stage.basePath));
        newRepeatableCounts[stageKey] = count;

        for (let i = 0; i < count; i++) {
          stage.fields.forEach((field) => {
            const fullPath = `${stage.basePath}[${i}] > ${field.path}`;
            const value = extractValueForField(xmlDoc, field, fullPath);
            values[fullPath] = value;
          });
        }

        return;
      }

      stage.fields.forEach((field) => {
        values[field.path] = extractValueForField(xmlDoc, field);
      });
    });

    setMetadataValues(values);
    setRepeatableStageCounts(newRepeatableCounts);
  };

  function getIso19139Path(iso19115Path) {
    return iso19115Path
      .replace(/mdb:/g, '')
      .replace(/mri:/g, '')
      .replace(/cit:/g, '')
      .replace(/mcc:/g, '')
      .replace(/msr:/g, '')
      .replace(/mrs:/g, '')
      .replace(/gex:/g, '')
      .replace(/lan:/g, '')
      .replace(/mrd:/g, '')
      .replace(/mdq:/g, '')
      .replace(/mco:/g, '')
      .replace(/mmi:/g, '')
      .replace('metadataIdentifier > MD_Identifier > code > gco:CharacterString', 'fileIdentifier > gco:CharacterString')
      .replace('MD_Identifier > code', 'RS_Identifier > code')
      .replace('defaultLocale > PT_Locale > language > LanguageCode', 'language > LanguageCode')
      .replace('defaultLocale > language > LanguageCode', 'language > LanguageCode')
      .replace('PT_Locale > language > LanguageCode', 'language > LanguageCode')
      .replace('defaultLocale > PT_Locale > characterEncoding > MD_CharacterSetCode', 'characterSet > MD_CharacterSetCode')
      .replace('defaultLocale > characterEncoding > MD_CharacterSetCode', 'characterSet > MD_CharacterSetCode')
      .replace('PT_Locale > characterEncoding > MD_CharacterSetCode', 'characterSet > MD_CharacterSetCode')
      .replace('metadataScope > MD_MetadataScope > resourceScope', 'hierarchyLevel')
      .replace('dateInfo > CI_Date > date > gco:DateTime', 'dateStamp > gco:Date')
      .replace('CI_Responsibility > role > CI_RoleCode', 'CI_ResponsibleParty > role > CI_RoleCode')
      .replace('CI_Responsibility > party > CI_Organisation > name', 'CI_ResponsibleParty > organisationName')
      .replace('CI_Responsibility > party > CI_Organisation > individual > CI_Individual > name', 'CI_ResponsibleParty > individualName')
      .replace('CI_Responsibility > party > CI_Organisation > individual > CI_Individual > positionName', 'CI_ResponsibleParty > positionName')
      .replace('CI_Responsibility > party > CI_Organisation > contactInfo', 'CI_ResponsibleParty > contactInfo')
      .replace('CI_Telephone > number', 'CI_Telephone > voice');
  }

  function isIso19139Document(xmlDoc) {
    if (!xmlDoc) return false;
    const root = xmlDoc.documentElement;
    const rootName = root?.tagName || root?.nodeName || '';
    if (rootName.includes('mdb:')) return false;
    if (rootName === 'MD_Metadata' || rootName.includes('gmd:')) return true;
    if (xmlDoc.querySelector('gmd\\:MD_Metadata') && !xmlDoc.querySelector('mdb\\:MD_Metadata')) return true;
    return detectedFormat === 'iso19139';
  }

  const getValueFromPath = (xmlDoc, path, attr) => {
    try {
      // Try ISO 19115-3 path first
      let value = getValueFromPathInternal(xmlDoc, path, attr);
      
      // If not found, try ISO 19139 path
      if (!value) {
        const iso19139Path = getIso19139Path(path);
        if (iso19139Path !== path) {
          value = getValueFromPathInternal(xmlDoc, iso19139Path, attr);
        }
      }
      
      return value;
    } catch (e) {
      console.error('Error getting value from path:', path, e);
      return '';
    }
  };

  const getValueFromPathInternal = (xmlDoc, path, attr) => {
    try {
      const element = resolveElementByPath(xmlDoc, path, { create: false });
      if (!element) return '';
      
      if (attr) {
        return element.getAttribute(attr) || element.getAttribute('codeListValue') || '';
      }
      
      return element.textContent?.trim() || '';
    } catch (e) {
      return '';
    }
  };

  const handleValueChange = (path, value) => {
    setMetadataValues(prev => ({
      ...prev,
      [path]: value
    }));
    setHasModified(true); // Mark as modified when user changes a value
  };

  const validateMetadata = () => {
    const errors = [];
    const stages = { ...METADATA_STAGES };

    Object.entries(stages).forEach(([stageKey, stage]) => {
      if (!stage.fields) return;

      if (stage.repeatable && stage.basePath) {
        const count = repeatableStageCounts[stageKey] || 1;
        for (let i = 0; i < count; i++) {
          stage.fields.forEach((field) => {
            if (!field.required) return;
            const fullPath = `${stage.basePath}[${i}] > ${field.path}`;
            const value = metadataValues[fullPath];
            if (!value || value.trim() === '' || value.includes('{{') || value.includes('[PLACEHOLDER]')) {
              errors.push({
                stage: stageKey,
                field: `${field.label} (Kontak #${i + 1})`,
                path: fullPath,
                message: `${field.label} (Kontak #${i + 1}) wajib diisi`
              });
            }
          });
        }
        return;
      }

      stage.fields.forEach((field) => {
        if (!field.required) return;
        const value = metadataValues[field.path];
        if (!value || value.trim() === '' || value.includes('{{') || value.includes('[PLACEHOLDER]')) {
          errors.push({
            stage: stageKey,
            field: field.label,
            path: field.path,
            message: `${field.label} wajib diisi`
          });
        }
      });
    });

    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleSave = async () => {
    const remainingPlaceholders = placeholders.filter(p => p.isPlaceholder);

    if (remainingPlaceholders.length > 0) {
      setShowPlaceholderWarning(true);
      return;
    }

    if (!validateMetadata()) {
      toast.error('Masih ada field wajib yang belum diisi');
      return;
    }

    setSaving(true);
    try {
      const updatedXml = buildUpdatedXml();
      
      if (onSave) {
        await onSave(updatedXml, metadataValues);
      }

      toast.success('Metadata berhasil disimpan');
      onClose();
    } catch (error) {
      console.error('Error saving metadata:', error);
      toast.error('Gagal menyimpan metadata');
    } finally {
      setSaving(false);
    }
  };

  const buildUpdatedXml = () => {
    if (!parsedXml) return originalXml;
    if (!hasModified) {
      return originalXml;
    }

    const xmlDoc = parsedXml.cloneNode(true);
    const serializer = new XMLSerializer();

    Object.entries(METADATA_STAGES).forEach(([stageKey, stage]) => {
      if (stage.repeatable && stage.basePath) {
        const desiredCount = repeatableStageCounts[stageKey] || 1;
        ensureRepeatableCount(xmlDoc, stage.basePath, desiredCount);
      }
    });

    Object.entries(metadataValues).forEach(([path, value]) => {
      if (value === null || value === undefined || value === '') return;

      const fieldDef = getFieldDefinitionForPath(path);
      setValueToPath(xmlDoc, path, value, fieldDef);
    });

    return serializer.serializeToString(xmlDoc);
  };

  const setValueToPath = (xmlDoc, path, value, fieldDef) => {
    if (fieldDef?.type === 'attrFromPath') {
      setValueToPathInternal(xmlDoc, fieldDef.targetPath, value, fieldDef.attr, true);
      return;
    }

    if (fieldDef?.type === 'dateByType') {
      setCitationDateByType(xmlDoc, fieldDef, value);
      return;
    }

    if (fieldDef?.type === 'typedKeywordList') {
      setTypedKeywordList(xmlDoc, fieldDef, value);
      return;
    }

    const effectivePath = fieldDef?.targetPath || path;
    const iso19139Path = getIso19139Path(effectivePath);
    const preferIso19139 = isIso19139Document(xmlDoc);

    const primaryPath = preferIso19139 ? iso19139Path : effectivePath;
    const secondaryPath = preferIso19139 ? effectivePath : iso19139Path;

    const trySet = (targetPath) => {
      if (fieldDef?.type === 'multiselect') {
        return setMultiSelectValuesToPath(xmlDoc, targetPath, value);
      }
      if (fieldDef?.type === 'list') {
        return setListValuesToPath(xmlDoc, targetPath, value);
      }
      return setValueToPathInternal(xmlDoc, targetPath, value, fieldDef?.attr, false) ||
        setValueToPathInternal(xmlDoc, targetPath, value, fieldDef?.attr, true);
    };

    let success = trySet(primaryPath);

    if (!success && secondaryPath && secondaryPath !== primaryPath) {
      trySet(secondaryPath);
    }
  };

  const setValueToPathInternal = (xmlDoc, path, value, attr, createMissing = true) => {
    try {
      const element = resolveElementByPath(xmlDoc, path, { create: createMissing });
      if (element) {
        if (attr) {
          element.setAttribute(attr, value);
          if (attr === 'codeListValue' && element.textContent?.trim()) {
            element.textContent = '';
          }
        } else if (element.hasAttribute('codeListValue')) {
          element.setAttribute('codeListValue', value);
          if (element.textContent?.trim()) {
            element.textContent = '';
          }
        } else {
          element.textContent = value;
        }
        return true;
      }
      return false;
    } catch (e) {
      console.error('Error setting value:', e);
      return false;
    }
  };

  const handleDownloadXml = () => {
    // If no modifications, download original file as-is
    const xmlToDownload = hasModified ? buildUpdatedXml() : originalXml;
    const blob = new Blob([xmlToDownload], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    
    // Use different filename to indicate if modified or original
    const filename = hasModified 
      ? `metadata_modified_${noTiket || 'export'}.xml`
      : `metadata_original_${noTiket || 'export'}.xml`;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Handle XML editing in XML view
  const handleXmlChange = (newXml) => {
    setEditableXml(newXml);
    setXmlError('');
  };

  // Apply XML changes from direct editing
  const tryApplyEditableXml = (silent = false) => {
    if (!editableXml || editableXml.trim() === '') return true;

    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(editableXml, 'text/xml');
      
      // Check for parsing errors
      const parseError = xmlDoc.querySelector('parsererror');
      if (parseError) {
        setXmlError('Error parsing XML: ' + parseError.textContent.substring(0, 200));
        if (!silent) toast.error('XML tidak valid');
        return false;
      }

      // Update parsedXml with new document
      setParsedXml(xmlDoc);
      setOriginalXml(editableXml);
      setEditableXml('');

      extractMetadataValues(xmlDoc);
      setHasModified(true);
      setXmlError('');
      if (!silent) alert('XML berhasil diterapkan!');
      return true;
    } catch (error) {
      setXmlError('Error: ' + error.message);
      if (!silent) toast.error('Gagal menerapkan XML');
      return false;
    }
  };

  const handleApplyXmlChanges = () => {
    tryApplyEditableXml(false);
  };

  const handleSwitchView = (nextView) => {
    if (activeView === 'xml' && nextView !== 'xml' && !readOnly) {
      const ok = tryApplyEditableXml(true);
      if (!ok) {
        setActiveView('xml');
        return;
      }
    }
    setActiveView(nextView);
  };

  // Handle tree node value editing
  const handleTreeNodeEdit = (nodePath, element) => {
    if (readOnly) return;
    const hasChildren = Array.from(element.children).length > 0;
    if (hasChildren) return; // Only edit leaf nodes
    
    setEditingNode(nodePath);
    setEditingNodeValue(element.textContent?.trim() || '');
  };

  // Save tree node value
  const handleTreeNodeSave = (element) => {
    if (element && editingNodeValue !== element.textContent?.trim()) {
      element.textContent = editingNodeValue;
      
      // Re-serialize XML
      const serializer = new XMLSerializer();
      const newXml = serializer.serializeToString(parsedXml.documentElement);
      setOriginalXml(newXml);
      setEditableXml('');
      setHasModified(true);

      extractMetadataValues(parsedXml);
    }
    setEditingNode(null);
    setEditingNodeValue('');
  };

  // Cancel tree node editing
  const handleTreeNodeCancel = () => {
    setEditingNode(null);
    setEditingNodeValue('');
  };

  // Check if a value is empty or placeholder
  const isEmptyOrPlaceholder = (value) => {
    if (!value) return true;
    const trimmed = value.trim();
    if (trimmed === '') return true;
    if (trimmed.includes('{{') || trimmed.includes('[PLACEHOLDER]')) return true;
    return false;
  };

  const handleApplyTemplate = async (templateType) => {
    // Langsung apply template - hanya mengisi field kosong/placeholder
    await applyTemplate(templateType);
  };

  const applyTemplate = async (templateType) => {
    setLoading(true);
    
    try {
      let templateXml = '';
      if (localTemplates && typeof localTemplates === 'object' && typeof localTemplates[templateType] === 'string') {
        templateXml = localTemplates[templateType];
      } else {
        if (disableRemoteTemplates) {
          toast.error('Template tidak tersedia pada mode publik');
          return;
        }

        const token = localStorage.getItem('token');
        const response = await axios.get(`${config.API_URL}/metadata/template/${templateType}`, {
          headers: { 'Authorization': `Bearer ${token}` },
          responseType: 'text'
        });
        templateXml = response.data;
      }

      // Parse template XML to extract values
      const parser = new DOMParser();
      const templateDoc = parser.parseFromString(templateXml, 'text/xml');
      
      // Get all stages and fields
      const stages = { ...METADATA_STAGES };
      if (templateType === 'raster') {
        Object.keys(RASTER_SPECIFIC_FIELDS).forEach(key => {
          if (stages[key]) {
            stages[key] = {
              ...stages[key],
              fields: [...stages[key].fields, ...RASTER_SPECIFIC_FIELDS[key].fields]
            };
          } else {
            stages[key] = RASTER_SPECIFIC_FIELDS[key];
          }
        });
      }

      // Extract template values
      const templateValues = {};
      const currentRepeatableCounts = {};
      Object.entries(stages).forEach(([stageKey, stage]) => {
        if (!stage.fields) return;

        if (stage.repeatable && stage.basePath) {
          currentRepeatableCounts[stageKey] = repeatableStageCounts[stageKey] || 1;
          const count = 1;
          for (let i = 0; i < count; i++) {
            stage.fields.forEach((field) => {
              const fullPath = `${stage.basePath}[${i}] > ${field.path}`;
              const value = extractValueForField(templateDoc, field, fullPath);
              if (value) {
                templateValues[fullPath] = value;
              }
            });
          }
          return;
        }

        stage.fields.forEach((field) => {
          const value = extractValueForField(templateDoc, field);
          if (value) {
            templateValues[field.path] = value;
          }
        });
      });

      // Expand template repeatable values to all instances (copy from [0])
      Object.entries(stages).forEach(([stageKey, stage]) => {
        if (!stage.repeatable || !stage.basePath || !stage.fields) return;
        const desiredCount = currentRepeatableCounts[stageKey] || 1;
        for (let i = 1; i < desiredCount; i++) {
          stage.fields.forEach((field) => {
            const baseKey = `${stage.basePath}[0] > ${field.path}`;
            const key = `${stage.basePath}[${i}] > ${field.path}`;
            if (templateValues[baseKey] && !templateValues[key]) {
              templateValues[key] = templateValues[baseKey];
            }
          });
        }
      });

      // Merge: only fill empty/placeholder fields, keep existing data
      const mergedValues = { ...metadataValues };
      let filledCount = 0;
      
      Object.entries(templateValues).forEach(([path, templateValue]) => {
        const currentValue = mergedValues[path];
        // Only replace if current value is empty or placeholder
        if (isEmptyOrPlaceholder(currentValue)) {
          mergedValues[path] = templateValue;
          filledCount++;
        }
      });

      setMetadataValues(mergedValues);
      setDataType(templateType);
      setHasModified(true); // Mark as modified after applying template

      // Build new standardized XML based on template structure, preserving mergedValues
      const newXmlDoc = templateDoc.cloneNode(true);
      Object.entries(METADATA_STAGES).forEach(([stageKey, stage]) => {
        if (stage.repeatable && stage.basePath) {
          const desiredCount = repeatableStageCounts[stageKey] || 1;
          ensureRepeatableCount(newXmlDoc, stage.basePath, desiredCount);
        }
      });

      Object.entries(mergedValues).forEach(([path, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          const fieldDef = getFieldDefinitionForPath(path);
          setValueToPath(newXmlDoc, path, value, fieldDef);
        }
      });

      const serializer = new XMLSerializer();
      const standardizedXml = serializer.serializeToString(newXmlDoc);
      setParsedXml(newXmlDoc);
      setOriginalXml(standardizedXml);
      setEditableXml('');
      setDetectedFormat('iso19115-3');
      setHasStandardized(true);
      extractMetadataValues(newXmlDoc);
      
      toast.success(`Template ${templateType} diterapkan - ${filledCount} field dilengkapi, data existing dipertahankan`);
    } catch (error) {
      console.error('Error loading template:', error);
      toast.error('Gagal memuat template');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadNewFile = (e) => {
    const file = e.target.files[0];
    if (file && file.name.toLowerCase().endsWith('.xml')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setOriginalXml(event.target.result);
        parseXmlContent(event.target.result);
      };
      reader.readAsText(file);
    } else {
      toast.error('Hanya file XML yang didukung');
    }
  };

  const handleApplySuggestion = (path, suggestion) => {
    handleValueChange(path, suggestion);
    toast.info('Suggestion diterapkan');
  };

  const navigateToPlaceholder = (placeholder) => {
    setActiveStage(placeholder.stage);
    handleSwitchView('form');
    
    setTimeout(() => {
      const fieldElement = fieldRefs.current[placeholder.path];
      if (fieldElement) {
        fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        fieldElement.classList.add('highlight-field');
        setTimeout(() => {
          fieldElement.classList.remove('highlight-field');
        }, 2000);
      }
    }, 100);
  };

  const handleStageClick = (stageKey) => {
    setActiveStage(stageKey);
    if (formContainerRef.current) {
      formContainerRef.current.scrollTop = 0;
    }
  };

  const getCurrentStageFields = () => {
    const stages = { ...METADATA_STAGES };
    
    if (dataType === 'raster' && RASTER_SPECIFIC_FIELDS[activeStage]) {
      return {
        ...stages[activeStage],
        fields: [...(stages[activeStage]?.fields || []), ...(RASTER_SPECIFIC_FIELDS[activeStage]?.fields || [])]
      };
    }
    
    return stages[activeStage];
  };

  const getPlaceholderCountForStage = (stageKey) => {
    return placeholders.filter(p => p.stage === stageKey).length;
  };

  const renderFormView = () => {
    const currentStage = getCurrentStageFields();
    if (!currentStage) return null;

    const isRepeatable = !!currentStage.repeatable;
    const repeatableCount = isRepeatable ? (repeatableStageCounts[activeStage] || 1) : 1;

    const handleAddRepeatableInstance = () => {
      if (readOnly || mode === 'view') return;
      const nextCount = repeatableCount + 1;
      setRepeatableStageCounts((prev) => ({
        ...prev,
        [activeStage]: nextCount
      }));
      setHasModified(true);
    };

    const handleRemoveRepeatableInstance = (removeIndex) => {
      if (readOnly || mode === 'view') return;
      if (repeatableCount <= 1) return;

      const stageDef = METADATA_STAGES[activeStage];
      if (!stageDef?.basePath || !stageDef?.fields) return;

      const nextValues = {};
      Object.entries(metadataValues).forEach(([key, val]) => {
        if (!key.startsWith(`${stageDef.basePath}[`)) {
          nextValues[key] = val;
        }
      });

      let newIndex = 0;
      for (let oldIndex = 0; oldIndex < repeatableCount; oldIndex++) {
        if (oldIndex === removeIndex) continue;
        stageDef.fields.forEach((field) => {
          const oldPath = `${stageDef.basePath}[${oldIndex}] > ${field.path}`;
          const newPath = `${stageDef.basePath}[${newIndex}] > ${field.path}`;
          nextValues[newPath] = metadataValues[oldPath] || '';
        });
        newIndex++;
      }

      setMetadataValues(nextValues);
      setRepeatableStageCounts((prev) => ({
        ...prev,
        [activeStage]: repeatableCount - 1
      }));
      setHasModified(true);
    };

    return (
      <div className="metadata-form-container" ref={formContainerRef}>
        <div className="metadata-form-header">
          <h3>
            {React.createElement(currentStage.icon, { size: 20 })}
            {currentStage.label}
          </h3>
          <p>{currentStage.description}</p>

          {isRepeatable && !readOnly && mode !== 'view' && (
            <div className="repeatable-actions">
              <button type="button" onClick={handleAddRepeatableInstance}>
                Tambah {currentStage.label}
              </button>
            </div>
          )}
        </div>

        {isRepeatable ? (
          <div className="repeatable-groups">
            {Array.from({ length: repeatableCount }).map((_, instanceIndex) => (
              <div key={instanceIndex} className="repeatable-group">
                <div className="repeatable-group-header">
                  <h4>{currentStage.label} #{instanceIndex + 1}</h4>
                  {!readOnly && mode !== 'view' && repeatableCount > 1 && (
                    <button
                      type="button"
                      className="repeatable-remove"
                      onClick={() => handleRemoveRepeatableInstance(instanceIndex)}
                    >
                      Hapus
                    </button>
                  )}
                </div>

                <div className="metadata-fields">
                  {currentStage.fields.map((field, index) => {
                    const fullPath = `${currentStage.basePath}[${instanceIndex}] > ${field.path}`;
                    const value = metadataValues[fullPath] || '';
                    const hasPlaceholder = value.includes('{{') || value.includes('[PLACEHOLDER]');
                    const isEmpty = field.required && (!value || value.trim() === '');

                    const keyedField = { ...field, path: fullPath };

                    return (
                      <div
                        key={`${instanceIndex}-${index}`}
                        className={`metadata-field ${field.required ? 'required' : ''} ${hasPlaceholder ? 'has-placeholder' : ''} ${isEmpty ? 'is-empty' : ''}`}
                        ref={(el) => (fieldRefs.current[fullPath] = el)}
                      >
                        <label>
                          {field.label}
                          {field.required && <span className="required-mark">*</span>}
                        </label>

                        {renderFieldInput(keyedField)}

                        {hasPlaceholder && (
                          <div className="placeholder-warning">
                            <AlertCircle size={14} />
                            <span>Field ini masih berisi placeholder</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="metadata-fields">
            {currentStage.fields.map((field, index) => {
              const value = metadataValues[field.path] || '';
              const hasPlaceholder = value.includes('{{') || value.includes('[PLACEHOLDER]');
              const isEmpty = field.required && (!value || value.trim() === '');
              
              return (
                <div 
                  key={index} 
                  className={`metadata-field ${field.required ? 'required' : ''} ${hasPlaceholder ? 'has-placeholder' : ''} ${isEmpty ? 'is-empty' : ''}`}
                  ref={el => fieldRefs.current[field.path] = el}
                >
                  <label>
                    {field.label}
                    {field.required && <span className="required-mark">*</span>}
                  </label>
                  
                  {renderFieldInput(field)}
                  
                  {!readOnly && suggestions[field.path] && (
                    <div className="field-suggestion">
                      <Wand2 size={14} />
                      <span>Saran: </span>
                      <button 
                        type="button"
                        onClick={() => handleApplySuggestion(field.path, suggestions[field.path])}
                      >
                        {suggestions[field.path]}
                      </button>
                    </div>
                  )}

                  {hasPlaceholder && (
                    <div className="placeholder-warning">
                      <AlertCircle size={14} />
                      <span>Field ini masih berisi placeholder</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderFieldInput = (field) => {
    const value = metadataValues[field.path] || '';
    const isReadOnly = readOnly || mode === 'view';

    switch (field.type) {
      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => handleValueChange(field.path, e.target.value)}
            disabled={isReadOnly}
          >
            <option value="">-- Pilih --</option>
            {field.options.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );

      case 'multiselect':
        const selectedValues = value ? value.split(',') : [];
        const pendingAdd = pendingMultiSelectAdds[field.path] || '';
        const multiselectDatalistId = `multiselect-add-${field.path.replace(/[^a-zA-Z0-9]/g, '_')}`;
        return (
          <div className="multiselect-container">
            {field.options.map(opt => (
              <label key={opt} className="multiselect-option">
                <input
                  type="checkbox"
                  checked={selectedValues.includes(opt)}
                  onChange={(e) => {
                    const newValues = e.target.checked
                      ? [...selectedValues, opt]
                      : selectedValues.filter(v => v !== opt);
                    handleValueChange(field.path, newValues.join(','));
                  }}
                  disabled={isReadOnly}
                />
                <span>{opt}</span>
              </label>
            ))}
            {!isReadOnly && (
              <div className="multiselect-add-row">
                <input
                  type="text"
                  value={pendingAdd}
                  placeholder="Tambah kategori (mis: environment)"
                  list={multiselectDatalistId}
                  onChange={(e) =>
                    setPendingMultiSelectAdds((prev) => ({
                      ...prev,
                      [field.path]: e.target.value
                    }))
                  }
                />
                <datalist id={multiselectDatalistId}>
                  {field.options
                    .filter((opt) => !selectedValues.includes(opt))
                    .map((opt) => (
                      <option key={opt} value={opt} />
                    ))}
                </datalist>
                <button
                  type="button"
                  onClick={() => {
                    const nextVal = pendingAdd.trim();
                    if (!nextVal) return;
                    if (!field.options.includes(nextVal)) {
                      toast.error('Kategori topik tidak valid (harus sesuai daftar ISO)');
                      return;
                    }
                    if (selectedValues.includes(nextVal)) {
                      setPendingMultiSelectAdds((prev) => ({ ...prev, [field.path]: '' }));
                      return;
                    }
                    const next = [...new Set([...selectedValues, nextVal])];
                    handleValueChange(field.path, next.join(','));
                    setPendingMultiSelectAdds((prev) => ({ ...prev, [field.path]: '' }));
                  }}
                >
                  Tambah
                </button>
              </div>
            )}
          </div>
        );

      case 'typedKeywordList':
        return renderFieldInput({ ...field, type: 'list' });

      case 'dateByType':
        return renderFieldInput({ ...field, type: 'date' });

      case 'list':
        const items = value
          ? value.split(',').map((s) => s.trim()).filter(Boolean)
          : [];
        const pending = pendingListInputs[field.path] || '';
        return (
          <div className="list-input-container">
            <div className="list-items">
              {items.map((item, idx) => (
                <div key={`${item}-${idx}`} className="list-item">
                  <span>{item}</span>
                  {!isReadOnly && (
                    <button
                      type="button"
                      onClick={() => {
                        const next = items.filter((_, i) => i !== idx);
                        handleValueChange(field.path, next.join(','));
                      }}
                    >
                      Hapus
                    </button>
                  )}
                </div>
              ))}
            </div>

            {!isReadOnly && (
              <div className="list-add-row">
                <input
                  type="text"
                  value={pending}
                  placeholder={field.placeholder}
                  onChange={(e) =>
                    setPendingListInputs((prev) => ({
                      ...prev,
                      [field.path]: e.target.value
                    }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const nextVal = pending.trim();
                      if (!nextVal) return;
                      const next = [...new Set([...items, nextVal])];
                      handleValueChange(field.path, next.join(','));
                      setPendingListInputs((prev) => ({ ...prev, [field.path]: '' }));
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    const nextVal = pending.trim();
                    if (!nextVal) return;
                    const next = [...new Set([...items, nextVal])];
                    handleValueChange(field.path, next.join(','));
                    setPendingListInputs((prev) => ({ ...prev, [field.path]: '' }));
                  }}
                >
                  Tambah
                </button>
              </div>
            )}
          </div>
        );

      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleValueChange(field.path, e.target.value)}
            placeholder={field.placeholder}
            disabled={isReadOnly}
            rows={4}
          />
        );

      case 'date':
        const displayDateValue = (() => {
          if (!value) return '';
          const trimmed = String(value).trim();
          if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return `${trimmed}T00:00`;
          return trimmed.length >= 16 ? trimmed.slice(0, 16) : '';
        })();

        const treatAsDateOnly = (() => {
          const iso19139Path = getIso19139Path(field.path);
          return iso19139Path.includes('gco:Date') && !iso19139Path.includes('gco:DateTime');
        })();

        return (
          <input
            type="datetime-local"
            value={displayDateValue}
            onChange={(e) => {
              const v = e.target.value;
              if (treatAsDateOnly) {
                handleValueChange(field.path, v ? v.slice(0, 10) : '');
              } else {
                handleValueChange(field.path, v ? v + ':00' : '');
              }
            }}
            disabled={isReadOnly}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleValueChange(field.path, e.target.value)}
            placeholder={field.placeholder}
            disabled={isReadOnly}
            step="any"
          />
        );

      case 'url':
        return (
          <input
            type="url"
            value={value}
            onChange={(e) => handleValueChange(field.path, e.target.value)}
            placeholder={field.placeholder}
            disabled={isReadOnly}
          />
        );

      default:
        return (
          <div className="input-with-suggestions">
            <input
              type="text"
              value={value}
              onChange={(e) => handleValueChange(field.path, e.target.value)}
              placeholder={field.placeholder}
              disabled={isReadOnly}
              list={field.suggestions ? `suggestions-${field.path.replace(/[^a-zA-Z0-9]/g, '_')}` : undefined}
            />
            {field.suggestions && (
              <datalist id={`suggestions-${field.path.replace(/[^a-zA-Z0-9]/g, '_')}`}>
                {field.suggestions.map(s => (
                  <option key={s} value={s} />
                ))}
              </datalist>
            )}
          </div>
        );
    }
  };

  const renderXmlView = () => {
    // Initialize editable XML if not set
    const currentXml = editableXml || buildUpdatedXml();
    
    return (
      <div className="metadata-xml-container">
        <div className="xml-toolbar">
          <button onClick={handleDownloadXml} className="btn-xml-action">
            <Download size={16} /> Download XML
          </button>
          {!disableXmlUpload && (
            <>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleUploadNewFile}
                accept=".xml"
                style={{ display: 'none' }}
              />
              <button 
                onClick={() => fileInputRef.current?.click()} 
                className="btn-xml-action"
                disabled={readOnly}
              >
                <Upload size={16} /> Upload XML
              </button>
            </>
          )}
          {!readOnly && (
            <button 
              onClick={handleApplyXmlChanges} 
              className="btn-xml-action btn-apply"
              title="Terapkan perubahan XML"
            >
              <Save size={16} /> Terapkan Perubahan
            </button>
          )}
        </div>
        {xmlError && (
          <div className="xml-error-banner">
            <AlertCircle size={16} />
            <span>{xmlError}</span>
          </div>
        )}
        {readOnly ? (
          <pre className="xml-content">
            <code>{currentXml}</code>
          </pre>
        ) : (
          <textarea
            className="xml-editor-textarea"
            value={editableXml || currentXml}
            onChange={(e) => handleXmlChange(e.target.value)}
            placeholder="Edit XML metadata di sini..."
            spellCheck={false}
          />
        )}
      </div>
    );
  };

  const renderTreeNode = (element, path = '', level = 0) => {
    if (!element || element.nodeType !== 1) return null;

    const nodePath = path ? `${path} > ${element.nodeName}` : element.nodeName;
    const hasChildren = Array.from(element.children).length > 0;
    const isExpanded = expandedNodes[nodePath];
    const textContent = !hasChildren ? element.textContent?.trim() : '';
    const isEditing = editingNode === nodePath;

    if (searchQuery && !element.outerHTML.toLowerCase().includes(searchQuery.toLowerCase())) {
      return null;
    }

    return (
      <div key={nodePath} className="tree-node" style={{ marginLeft: level * 16 }}>
        <div 
          className={`tree-node-header ${!hasChildren && !readOnly ? 'editable' : ''}`}
          onClick={() => hasChildren && setExpandedNodes(prev => ({ ...prev, [nodePath]: !prev[nodePath] }))}
        >
          {hasChildren ? (
            isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />
          ) : <span className="tree-spacer" />}
          <span className="node-name">{element.nodeName}</span>
          {!hasChildren && (
            isEditing ? (
              <span className="node-value-edit" onClick={(e) => e.stopPropagation()}>
                <input
                  type="text"
                  value={editingNodeValue}
                  onChange={(e) => setEditingNodeValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleTreeNodeSave(element);
                    } else if (e.key === 'Escape') {
                      handleTreeNodeCancel();
                    }
                  }}
                  autoFocus
                  className="tree-edit-input"
                />
                <button 
                  className="tree-edit-btn save" 
                  onClick={() => handleTreeNodeSave(element)}
                  title="Simpan (Enter)"
                >
                  ✓
                </button>
                <button 
                  className="tree-edit-btn cancel" 
                  onClick={handleTreeNodeCancel}
                  title="Batal (Esc)"
                >
                  ✕
                </button>
              </span>
            ) : (
              <span 
                className={`node-value ${!readOnly ? 'clickable' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!readOnly) handleTreeNodeEdit(nodePath, element);
                }}
                title={!readOnly ? 'Klik untuk edit' : ''}
              >
                {textContent || <em className="empty-value">(kosong)</em>}
                {!readOnly && <Edit3 size={12} className="edit-hint" />}
              </span>
            )
          )}
          {element.attributes.length > 0 && (
            <span className="node-attrs">
              {Array.from(element.attributes).map(attr => (
                <span key={attr.name} className="attr">{attr.name}="{attr.value}"</span>
              ))}
            </span>
          )}
        </div>
        {hasChildren && isExpanded && (
          <div className="tree-children">
            {Array.from(element.children).map((child, idx) => 
              renderTreeNode(child, nodePath, level + 1)
            )}
          </div>
        )}
      </div>
    );
  };

  const renderTreeView = () => (
    <div className="metadata-tree-container">
      <div className="tree-search">
        <Search size={16} />
        <input
          type="text"
          placeholder="Cari elemen..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>
      <div className="tree-content">
        {parsedXml && renderTreeNode(parsedXml.documentElement)}
      </div>
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="metadata-editor-overlay">
      <div className="metadata-editor-modal">
        {/* Header */}
        <div className="metadata-editor-header">
          <div className="header-left">
            <FileCode size={24} />
            <div>
              <h2>Metadata Editor</h2>
              <span className="data-type-badge">{dataType === 'raster' ? 'Raster' : 'Vector'} Data</span>
            </div>
          </div>
          <div className="header-actions">
            <div className="view-toggle">
              <button 
                className={activeView === 'form' ? 'active' : ''} 
                onClick={() => handleSwitchView('form')}
              >
                <Edit3 size={16} /> Form
              </button>
              <button 
                className={activeView === 'tree' ? 'active' : ''} 
                onClick={() => handleSwitchView('tree')}
              >
                <List size={16} /> Tree
              </button>
              <button 
                className={activeView === 'xml' ? 'active' : ''} 
                onClick={() => handleSwitchView('xml')}
              >
                <Code size={16} /> XML
              </button>
            </div>
            <button className="metadata-close-btn" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Template Bar */}
        {!readOnly && !hideTemplateBar && (!disableRemoteTemplates || !!localTemplates) && (
          <div className="template-bar">
            <span>Standarisasi Template:</span>
            <button onClick={() => handleApplyTemplate('vector')} className={dataType === 'vector' ? 'active' : ''}>
              <Database size={14} /> Vector (ISO 19115-3)
            </button>
            <button onClick={() => handleApplyTemplate('raster')} className={dataType === 'raster' ? 'active' : ''}>
              <Layers size={14} /> Raster (ISO 19115-3)
            </button>
            <div className="data-type-toggle">
              <span>Tipe Data:</span>
              <select value={dataType} onChange={(e) => setDataType(e.target.value)}>
                <option value="vector">Vector</option>
                <option value="raster">Raster</option>
              </select>
            </div>
          </div>
        )}

        {/* Format Info Banner */}
        <div className={`format-info-banner ${detectedFormat === 'iso19115-3' ? 'compliant' : 'non-compliant'}`}>
          <div className="format-info-content">
            <div className="format-detected">
              <Info size={16} />
              <span>
                <strong>Format Terdeteksi:</strong> {' '}
                {detectedFormat === 'iso19115-3' ? 'ISO 19115-3:2014 (Sesuai PerBIG No. 3)' : 
                 detectedFormat === 'iso19139' ? 'ISO 19139:2007 (Legacy)' : 'Format tidak dikenali'}
              </span>
            </div>
            
            {detectedFormat !== 'iso19115-3' && !hasStandardized && (
              <div className="format-recommendation">
                <AlertTriangle size={16} />
                <span>
                  Metadata ini belum sesuai standar PerBIG No. 3 Tahun 2023 (ISO 19115-3). 
                  <strong> Klik tombol template di atas untuk standarisasi.</strong>
                </span>
              </div>
            )}

            {hasModified && (
              <div className="modification-status modified">
                <Edit3 size={16} />
                <span>
                  {hasStandardized
                    ? 'Metadata telah distandarisasi. Download XML akan menyertakan perubahan.'
                    : 'Metadata telah diubah. Download XML akan menyertakan perubahan.'}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Placeholder Warning - Only show after template applied (hasModified) */}
        {hasModified && placeholders.length > 0 && (
          <div className="placeholder-alert">
            <AlertTriangle size={18} />
            <span>{placeholders.length} field masih kosong/placeholder yang perlu diisi</span>
            <button onClick={() => setShowPlaceholderWarning(true)}>Lihat Detail & Navigasi</button>
          </div>
        )}

        {/* Main Content */}
        <div className="metadata-editor-body">
          {loading ? (
            <div className="metadata-loading">
              <RefreshCw className="spin" size={32} />
              <p>Memuat metadata...</p>
            </div>
          ) : (
            <>
              {/* Sidebar Navigation */}
              {activeView === 'form' && (
                <div className="metadata-sidebar">
                  <div className="sidebar-stages">
                    {Object.entries(METADATA_STAGES).map(([key, stage]) => {
                      const stageErrors = validationErrors.filter(e => e.stage === key);
                      const stagePlaceholderCount = getPlaceholderCountForStage(key);
                      
                      return (
                        <button
                          key={key}
                          className={`stage-btn ${activeStage === key ? 'active' : ''} ${stageErrors.length > 0 ? 'has-error' : ''}`}
                          onClick={() => handleStageClick(key)}
                          type="button"
                        >
                          {React.createElement(stage.icon, { size: 18 })}
                          <span>{stage.label}</span>
                          {stagePlaceholderCount > 0 && (
                            <span className="placeholder-count">{stagePlaceholderCount}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Main Content Area */}
              <div className="metadata-content">
                {activeView === 'form' && renderFormView()}
                {activeView === 'xml' && renderXmlView()}
                {activeView === 'tree' && renderTreeView()}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="metadata-editor-footer">
          <div className="footer-left">
            {validationErrors.length > 0 && (
              <span className="validation-summary">
                <AlertCircle size={16} />
                {validationErrors.length} error validasi
              </span>
            )}
          </div>
          <div className="footer-actions">
            <button className="btn-secondary" onClick={onClose}>
              Batal
            </button>
            {!readOnly && (
              <button 
                className="btn-primary" 
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <RefreshCw className="spin" size={16} />
                    Menyimpan...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Simpan Metadata
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Placeholder Warning Modal */}
        {showPlaceholderWarning && (
          <div className="placeholder-modal-overlay">
            <div className="placeholder-modal">
              <div className="placeholder-modal-header">
                <AlertTriangle size={24} />
                <h3>Placeholder Perlu Diisi</h3>
                <button onClick={() => setShowPlaceholderWarning(false)}>
                  <X size={20} />
                </button>
              </div>
              <div className="placeholder-modal-body">
                <p>Field berikut masih berisi placeholder atau kosong:</p>
                <ul className="placeholder-list">
                  {placeholders.map((p, idx) => (
                    <li key={idx}>
                      <span className="placeholder-stage">{METADATA_STAGES[p.stage]?.label}</span>
                      <span className="placeholder-field">{p.field}</span>
                      <span className={`placeholder-type ${p.isPlaceholder ? 'is-placeholder' : 'is-empty'}`}>
                        {p.isPlaceholder ? 'Placeholder' : 'Kosong'}
                      </span>
                      <button onClick={() => {
                        navigateToPlaceholder(p);
                        setShowPlaceholderWarning(false);
                      }}>
                        <Navigation size={14} /> Navigasi
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="placeholder-modal-footer">
                <button 
                  className="btn-secondary" 
                  onClick={() => setShowPlaceholderWarning(false)}
                >
                  Tutup
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MetadataEditor;